import React, {useEffect, useState} from 'react';
import {
    differenceInCalendarMonths,
    endOfDay,
    formatISO,
    isBefore,
    startOfDay,
    startOfToday,
} from 'date-fns';
import DayPicker from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import './DateRangePickerBox.scss';
import {Translate} from '../../../../config/TranslationProvider';

const DateRangePickerBox = ({startDate, endDate, onChange, translations}) => {
    const [from, setFrom] = useState(new Date(startDate));
    const [to, setTo] = useState(new Date(endDate)); // Keep track of the last hovered day before the user clicks it
    const [selectedMonth, setSelectedMonth] = useState(from);
    const [isSelecting, setIsSelecting] = useState(false);

    const [focusedDay, setFocusedDay] = useState(null);

    // Update from and to when the start or end date changes
    useEffect(() => {
        if (formatISO(from) !== startDate) {
            const from = new Date(startDate);
            setFrom(from);

            // Update selected left month only if the new from is not the next month which would be on the right side
            if (differenceInCalendarMonths(from, selectedMonth) !== 1) {
                setSelectedMonth(from);
            }
        }
        if (formatISO(to) !== endDate) {
            setTo(new Date(endDate));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    function handleClick(day) {
        const date = startOfDay(day);

        if (!isSelecting || isBefore(date, from)) {
            setIsSelecting(true);
            setFrom(date);
            setTo(endOfDay(date));
        } else {
            setIsSelecting(false);
            const newTo = endOfDay(date);
            setTo(newTo);

            // We updated both start and end date, call onChange with the new values
            onChange(formatISO(from), formatISO(newTo));
        }
    }

    function handleHover(day) {
        if (!isSelecting) {
            return;
        }

        let date = startOfDay(day);

        if (isBefore(date, from)) {
            date = from;
        }

        setTo(date);
    }

    return (
        <>
            <DayPicker
                className="Range"
                months={translations && translations['months']}
                weekdaysLong={translations && translations['weekdaysLong']}
                weekdaysShort={translations && translations['weekdaysShort']}
                labels={translations && translations['labels']}
                firstDayOfWeek={1}
                numberOfMonths={2}
                fromMonth={new Date()}
                disabledDays={{before: startOfToday()}}
                month={selectedMonth}
                selectedDays={{from, to: to}}
                onDayFocus={(day) => setFocusedDay(day)}
                modifiers={{start: from, end: to, focused: focusedDay}}
                modifiersStyles={{
                    focused: {
                        color: 'red'
                    }
                }}
                onDayClick={handleClick}
                onDayMouseEnter={handleHover}
                autoFocus
                role="application"
                aria-describedby="date-range-description"
            />
            <span id="date-range-description" className="sr-only">
                <Translate value="date_range_picker_description" />
            </span>
        </>
    );
};

export default DateRangePickerBox;
